/**
 * R158u · Feb 2026 — Accounts Notes header indicator.
 *
 * Small nav button next to WatchListHeaderButton showing an unread count
 * badge whenever new billing notes have been posted by someone else since
 * the current user's last "read" marker. Clicking the button opens a
 * popover with the 5 most recent unread notes and a "Mark all read"
 * action that resets the marker server-side.
 *
 * Backend contract (see routes_billing_notes.py):
 *   GET  /api/v1/workspaces/{ws}/billing-notes/unread-count
 *          → { count, last_read_at, latest: [{ scope, scope_id, body, author_name, created_at }] }
 *   POST /api/v1/workspaces/{ws}/billing-notes/mark-all-read
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquarePlus, CheckCheck } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import api from "@/lib/api";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

const POLL_MS = 60_000;

export default function NotesHeaderButton() {
  const { activeWorkspaceId } = useAuth();
  const [count, setCount] = useState(0);
  const [latest, setLatest] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/billing-notes/unread-count`,
      );
      setCount(data?.count || 0);
      setLatest(data?.latest || []);
    } catch { /* silent — RBAC-restricted users get 0 count from the backend */ }
  }, [activeWorkspaceId]);

  useEffect(() => {
    load();
    // Feb 2026 R281 · Visibility-gated polling.
    return pollWhenVisible(load, POLL_MS);
  }, [load]);

  const markAllRead = async () => {
    if (!activeWorkspaceId || busy) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/billing-notes/mark-all-read`);
      setCount(0);
      setLatest([]);
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative p-1.5 hover:bg-neutral-100 rounded-none text-neutral-600 hover:text-primary transition-colors"
          title="Accounts notes"
          aria-label={count > 0 ? `${count} unread Accounts notes` : "Accounts notes"}
          data-testid="notes-header-button"
        >
          <MessageSquarePlus className="w-4 h-4" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-none flex items-center justify-center"
              data-testid="notes-header-badge"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom" align="end"
        className="w-80 p-0 rounded-none"
        data-testid="notes-header-popover"
      >
        <div className="px-3 py-2 border-b border-border flex items-center gap-2">
          <MessageSquarePlus className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold flex-1">Accounts notes</span>
          {count > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              disabled={busy}
              className="text-[10px] uppercase tracking-widest text-primary hover:underline disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="notes-header-mark-read"
            >
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        {latest.length === 0 ? (
          <div className="p-4 text-center text-[11px] text-muted-foreground italic">
            {count === 0 ? "You're all caught up." : "No preview available."}
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto divide-y divide-border">
            {latest.map((n) => (
              <li
                key={n.id || `${n.scope}-${n.scope_id}-${n.created_at}`}
                className="px-3 py-2 hover:bg-muted/40"
                data-testid={`notes-header-item-${n.id || n.scope_id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] uppercase tracking-widest border border-border px-1 py-0.5 text-muted-foreground">
                    {n.scope}
                  </span>
                  <span className="text-[10px] text-muted-foreground truncate">
                    {n.author_name}
                  </span>
                  <span className="text-[10px] text-muted-foreground/70 ml-auto shrink-0">
                    {fmtRelative(n.created_at)}
                  </span>
                </div>
                <div className="text-xs leading-snug line-clamp-3 break-words">
                  {n.body}
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground">
          Open a project or task Notes button to reply.
        </div>
      </PopoverContent>
    </Popover>
  );
}
